import { EntityState, EntityAdapter, createEntityAdapter } from '@ngrx/entity'
import { createReducer, on, Action } from '@ngrx/store'

import * as FavouriteItemsActions from './favourite-items.actions'
import { FavouriteItemsEntity } from './favourite-items.models'
import { environment } from 'apps/gb-front/src/environments/environment'
import { SlimItemEntity } from 'libs/gb-front/item/data-access/src/lib/+state/items.models'

export const FAVOURITE_ITEMS_FEATURE_KEY = 'favouriteItems'

export interface FavouriteItemsState extends EntityState<FavouriteItemsEntity> {
  selectedId?: string | number // which FavouriteItems record has been selected
  loaded: boolean // has the FavouriteItems list been loaded
  enableFavourite: boolean // should the like button be enabled
  count: number // total number of FavouriteItems
  order: string // order_hash of FavoriteItems
  stale: number[] // list of stale FavouriteItems pages
}

export interface FavouriteItemsPartialState {
  readonly [FAVOURITE_ITEMS_FEATURE_KEY]: FavouriteItemsState
}

export const favouriteItemsAdapter: EntityAdapter<FavouriteItemsEntity> = createEntityAdapter<FavouriteItemsEntity>({
  selectId: (item: FavouriteItemsEntity) => item.uuid,
  sortComparer: (a: FavouriteItemsEntity, b: FavouriteItemsEntity) => (a.order < b.order ? 0 : 1),
})

export const initialState: FavouriteItemsState = favouriteItemsAdapter.getInitialState({
  // set initial required properties
  enableFavourite: true,
  loaded: false,
  count: 0,
  order: '',
  stale: [],
})

const favouriteItemsReducer = createReducer(
  initialState,
  on(FavouriteItemsActions.loadFavouriteItems, (state) => ({
    ...state,
    loaded: false,
    error: null,
  })),
  on(FavouriteItemsActions.upsertFavouriteItems, (state, { favouriteItems }) =>
    favouriteItemsAdapter.upsertMany(getListItems(favouriteItems, false), {
      ...state,
      loaded: true,
      enableFavourite: true,
    }),
  ),
  on(FavouriteItemsActions.updateCount, (state, action) => ({
    ...state,
    count: action.count,
  })),
  on(FavouriteItemsActions.updateOrderHash, (state, action) => ({
    ...state,
    order: action.order,
  })),
  on(FavouriteItemsActions.setAllFavouriteItemsStale, (state) =>
    favouriteItemsAdapter.setAll(getListItems([...selectAll(state)], true), {
      ...state,
      stale: getPages(state.ids),
    }),
  ),
  on(FavouriteItemsActions.removePageFromStale, (state, action) => ({
    ...state,
    // remove resynced page from stale pages
    stale: state.stale.filter((page) => page !== action.page),
  })),
  on(FavouriteItemsActions.deleteFavouriteItem, (state, action) =>
    favouriteItemsAdapter.removeOne(action.itemUuid, state),
  ),
  on(FavouriteItemsActions.createFavouriteItemSuccess, (state, action) =>
    favouriteItemsAdapter.addOne(action.item, state),
  ),
  on(FavouriteItemsActions.updateFavouriteItem, (state, action) =>
    favouriteItemsAdapter.updateOne(action.update, state),
  ),
  on(FavouriteItemsActions.enableFavourite, (state) => ({
    ...state,
    enableFavourite: true,
  })),
  on(FavouriteItemsActions.disableFavourite, (state) => ({
    ...state,
    enableFavourite: false,
  })),
  on(FavouriteItemsActions.removeFavouriteItems, (state) => ({
    ...state,
    ...favouriteItemsAdapter.removeAll(state),
  })),
  on(FavouriteItemsActions.clearFavouriteItems, () => ({
    ...initialState,
  })),
)

/**
 * Adds isStale property to each favourite item.
 */
export const getListItems = (items: SlimItemEntity[], isStale: boolean) => items.map((item) => ({ ...item, isStale }))

export const getPages = (arr: string[] | number[]) => {
  return arr
    .map((_, index: number) => (index % environment.itemsPageSize === 0 ? index : null))
    .filter((i): i is number => i !== null)
}

export function reducer(state: FavouriteItemsState | undefined, action: Action) {
  return favouriteItemsReducer(state, action)
}

export const { selectIds, selectEntities, selectAll, selectTotal } = favouriteItemsAdapter.getSelectors()
